import React, { useEffect } from 'react';
import { BrowserRouter, MemoryRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { CssBaseline } from '@mui/material';
import './shared/design/global.css';
import './shared/design/chartFixes.css';
import HomePage from './screens/home/HomePage';
import ProductPage from './screens/product/ProductPage';
import ExcelPage from './screens/product/ExcelPage';
import WordPage from './screens/product/WordPage';
import SettingsScreen from './screens/settings/SettingsScreen';
import ConsoleSignIn from './shared/components/ConsoleSignIn';
import { isConsoleHost } from './shared/utils';
import ChatScreen from './screens/chat/ChatScreen';
import AuthDialog from './screens/chat/AuthDialog';
import PrivacyPage from './screens/legal/PrivacyPage';
import TermsPage from './screens/legal/TermsPage';
import SecurityPage from './screens/legal/SecurityPage';
import PricingPage from './screens/pricing/PricingPage';
import BlogPostPage from './screens/blog/BlogPostPage';
import BlogIndexPage from './screens/blog/BlogIndexPage';
import ComparePage from './screens/compare/ComparePage';
import ClaudeComparePage from './screens/compare/ClaudeComparePage';
import CopilotComparePage from './screens/compare/CopilotComparePage';
import UseCasePage from './screens/use-cases/UseCasePage';
import AudiencePage from './screens/use-cases/AudiencePage';
import CompanyPage from './screens/company/CompanyPage';
import AITransformationPage from './screens/ai-transformation/AITransformationPage';
import DemoPage from './screens/demo/DemoPage';
// JoinPodScreen import removed: /join is now a thin redirect to
// /settings?inviteCode=... — the pod-join input lives there. See route below.
import { UserProvider, useUser } from './shared/contexts/UserContext';
import { UIProvider } from './shared/contexts/UIContext';
import { analytics } from './shared/services/analytics';
import { initPostHog, capturePageView } from './shared/services/posthog';

// /admin moved into Settings (Organization section). Preserve the query so
// /admin?welcome=1 (post-checkout) lands on the welcome checklist.
function AdminRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/settings/members', search: location.search }} replace />;
}

// The console lives on its own subdomain once REACT_APP_CONSOLE_URL is set:
// /settings on the MAIN domain then forwards there (path + query preserved),
// so auth and account UI never run on re-stack.com itself. Unset (local dev,
// pre-DNS), the console serves same-origin as before.
function ConsoleGate({ children }) {
  const location = useLocation();
  const consoleBase = (process.env.REACT_APP_CONSOLE_URL || '').replace(/\/+$/, '');
  if (consoleBase && !isConsoleHost()) {
    window.location.replace(`${consoleBase}${location.pathname}${location.search}`);
    return null;
  }
  return children;
}

// Detect if running inside Office add-in iframe (no history.replaceState)
const isOfficeAddin = window.location.pathname.startsWith('/chat');

// Initialize PostHog at app startup
initPostHog();

// Load analytics test helper in development
if (process.env.NODE_ENV === 'development') {
  import('./shared/utils');
}

// Component to handle analytics page tracking
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    // Initialize analytics on first load
    analytics.initialize();
  }, []);

  useEffect(() => {
    // Track page views on route changes
    const getPageTitle = (pathname) => {
      switch (pathname) {
        case '/':
          return 'Landing - Restack';
        case '/settings':
          return 'Settings - Restack';
        case '/chat':
          return 'Chat - Restack';
        case '/privacy':
          return 'Privacy Policy - Restack';
        case '/terms':
          return 'Terms of Service - Restack';
        case '/company':
          return 'Company - Restack';
        case '/ai-transformation':
          return 'AI Transformation - Restack';
        case '/news':
          return 'News - Restack';
        case '/join':
          // /join is now a redirect to /settings; the title flash is brief.
          return 'Restack';
        default:
          if (pathname.startsWith('/blog/')) return 'Blog - Restack';
          if (pathname.startsWith('/vs/') || pathname.startsWith('/compare/')) return 'Compare - Restack';
          if (pathname === '/solutions' || pathname.startsWith('/solutions/')
            || pathname === '/use-cases' || pathname.startsWith('/use-cases/')) return 'Solutions - Restack';
          return 'Restack';
      }
    };

    const title = getPageTitle(location.pathname);
    analytics.trackPageView(location.pathname, title);
    capturePageView(location.pathname, title);
  }, [location]);

  return null;
}

// Protected route wrapper - redirects unauthenticated users to /
function ProtectedRoute({ children }) {
  const { isAuthenticated, authLoading } = useUser();

  // While loading auth, show nothing
  if (authLoading) {
    return null;
  }

  if (isAuthenticated) {
    return children;
  }

  // Signed-out visitors get the console sign-in IN PLACE (the landing page
  // only links here; authentication happens on the console). Rendering
  // instead of redirecting also avoids a redirect loop on the console
  // subdomain, where '/' itself forwards to /settings.
  return <ConsoleSignIn />;
}

/**
 * /join?code=POD-XXXX — historical invite-email deep link.
 *
 * Now a thin redirect to /settings?inviteCode=POD-XXXX. The Settings page
 * picks up the query param and pre-fills the "Join with code" input. We
 * deliberately don't bake the join logic into a separate page: signup
 * provider should be the user's choice (Google, Microsoft, email), and
 * the actual join is gated by email-match against the pod's pendingInvites,
 * not by which auth provider they used.
 */
function JoinRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const code = params.get('code');
  // Invite codes use their own query key. `?code=` is reserved for sales
  // coupons, so sharing one key here could redeem a pod invite as a coupon.
  const target = code
    ? `/settings?inviteCode=${encodeURIComponent(code)}`
    : '/settings';
  return <Navigate to={target} replace />;
}

/**
 * /use-cases/:slug — legacy BOFU URL. Redirects to /solutions/:slug, keeping
 * the slug, so links already shared with customers keep working after the
 * rename. (Plain <Navigate> can't substitute the param, hence this wrapper.)
 */
function UseCaseSlugRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/solutions/${slug}`} replace />;
}

/**
 * /advisory, the page's old URL. Keeps the hash so /advisory#form links still
 * land on the form. (Plain <Navigate> drops it, hence this wrapper.)
 */
function AdvisoryRedirect() {
  const { hash } = useLocation();
  return <Navigate to={{ pathname: '/ai-transformation', hash }} replace />;
}

function App() {
  // Office add-in iframe doesn't have history.replaceState — use MemoryRouter.
  // HelmetProvider wraps both branches so any future SEO inside the
  // task pane (or shared components) doesn't blow up when used.
  if (isOfficeAddin) {
    const initialPath = window.location.pathname; // /chat or /chat/auth
    return (
      <HelmetProvider>
        <UserProvider>
          <CssBaseline />
          <MemoryRouter initialEntries={[initialPath]}>
            <div className="App">
              <Routes>
                <Route path="/chat" element={<ChatScreen />} />
                <Route path="/chat/auth" element={<AuthDialog />} />
              </Routes>
            </div>
          </MemoryRouter>
        </UserProvider>
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider>
      <UserProvider>
        <CssBaseline />
        <UIProvider>
          <BrowserRouter>
          <AnalyticsTracker />
          <div className="App">
            <Routes>
              {/* Home page (website root) */}
              {/* On the console subdomain the root IS the console — no
                  marketing page there (the Anthropic/OpenAI pattern). */}
              <Route path="/" element={isConsoleHost() ? <Navigate to="/settings" replace /> : <HomePage />} />

              {/* Standalone demo video page — shareable URL for LinkedIn/Twitter
                  with proper SEO/OG card. Wraps the same /videos/restack-demo.mp4
                  used on the home page inside a branded shell so link previews
                  carry the Restack identity instead of showing a raw .mp4. */}
              <Route path="/demo" element={<DemoPage />} />

              {/* Product — dedicated product deep-dive (video + capability
                  showcase + core capabilities). The "Product" nav points here. */}
              <Route path="/product" element={<ProductPage />} />
              {/* Secondary product surfaces: the Excel and Word add-ins + the
                  cross-app story. The three surfaces under the Product menu. */}
              <Route path="/excel" element={<ExcelPage />} />
              <Route path="/word" element={<WordPage />} />

              {/* Settings - protected route. Console-style shell: a left
                  sidebar of routed sections (account / workspace / plan /
                  organization, the last with admin sub-sections). The same
                  component serves all of them and renders the active section
                  from the URL params. */}
              <Route path="/settings" element={
                <ConsoleGate>
                  <ProtectedRoute>
                    <SettingsScreen />
                  </ProtectedRoute>
                </ConsoleGate>
              } />
              <Route path="/settings/:section" element={
                <ConsoleGate>
                  <ProtectedRoute>
                    <SettingsScreen />
                  </ProtectedRoute>
                </ConsoleGate>
              } />
              <Route path="/settings/:section/:sub" element={
                <ConsoleGate>
                  <ProtectedRoute>
                    <SettingsScreen />
                  </ProtectedRoute>
                </ConsoleGate>
              } />

              {/* Legacy Admin panel URL — the org admin surface now lives
                  inside Settings (Organization section). Redirect preserves
                  the query string so /admin?welcome=1 keeps working. */}
              <Route path="/admin" element={<AdminRedirect />} />

              {/* Plugin chat */}
              <Route path="/chat" element={<ChatScreen />} />
              <Route path="/chat/auth" element={<AuthDialog />} />

              {/* News / blog. Articles keep /blog/:slug URLs; the index is
                  reachable at both /news (canonical) and /blog (legacy). */}
              <Route path="/news" element={<BlogIndexPage />} />
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />

              {/* BOFU comparison pages. Canonical short URL is /vs/<competitor>
                  (e.g. /vs/claude); /compare/restack-vs-* stays as a legacy
                  alias. Slug-driven from comparisonsData.js.

                  /vs/claude and /vs/copilot have their own dedicated pages
                  (ClaudeComparePage, CopilotComparePage); every other
                  competitor uses the generic table-driven ComparePage. */}
              <Route path="/vs/claude" element={<ClaudeComparePage />} />
              <Route path="/compare/restack-vs-claude" element={<ClaudeComparePage />} />
              <Route path="/vs/copilot" element={<CopilotComparePage />} />
              <Route path="/compare/restack-vs-copilot" element={<CopilotComparePage />} />
              <Route path="/vs/:slug" element={<ComparePage />} />
              <Route path="/compare/:slug" element={<ComparePage />} />

              {/* The combined Solutions overview was retired in favor of the
                  three dedicated audience pages below. The bare /solutions URL
                  (and legacy /use-cases) redirect home so previously shared
                  links are not orphaned. */}
              <Route path="/solutions" element={<Navigate to="/" replace />} />

              {/* Audience pages — folded-over, focused versions of the
                  /solutions overview in the same design language. Static routes
                  take precedence over the dynamic /solutions/:slug below. */}
              <Route path="/solutions/private-equity" element={<AudiencePage slug="private-equity" />} />
              <Route path="/solutions/growth-equity" element={<AudiencePage slug="growth-equity" />} />
              <Route path="/solutions/consulting" element={<AudiencePage slug="consulting" />} />

              {/* BOFU solution pages — /solutions/ai-ic-memo-builder etc.
                  Slug-driven from useCasesData.js. */}
              <Route path="/solutions/:slug" element={<UseCasePage />} />

              {/* Legacy /use-cases URLs, kept alive (already shared with
                  customers). The index redirects home; slug URLs still resolve
                  to their /solutions/:slug equivalents. */}
              <Route path="/use-cases" element={<Navigate to="/" replace />} />
              <Route path="/use-cases/:slug" element={<UseCaseSlugRedirect />} />

              {/* Pod invite deep-link — redirects to /settings with the
                  invite code preserved as a query param. The Settings page
                  has the "Join with code" input that handles the actual
                  join. Kept as a redirect so any old invite emails still
                  work; new invites point directly at /settings. */}
              <Route path="/join" element={<JoinRedirect />} />

              {/* Public legal + support pages */}
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/pricing" element={<PricingPage />} />

              {/* Unified Company page (Careers + Support + News as anchored
                  sections). The old /careers and /support routes redirect into
                  it so shared links keep working; /news stays as the archive. */}
              <Route path="/company" element={<CompanyPage />} />

              {/* AI transformation: assess, build, adopt. The old /advisory
                  URL redirects here (a 301 in firebase.json, this route for
                  in-app navigation), keeping any #form hash. */}
              <Route path="/ai-transformation" element={<AITransformationPage />} />
              <Route path="/advisory" element={<AdvisoryRedirect />} />
              <Route path="/careers" element={<Navigate to="/company#careers" replace />} />
              <Route path="/support" element={<Navigate to="/company#support" replace />} />

              {/* Default — show home page */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
          </BrowserRouter>
        </UIProvider>
      </UserProvider>
    </HelmetProvider>
  );
}

export default App;
